import React, { useEffect, useRef, useState } from 'react';
import { Box, Text, Button, Stack, Center, Group, Modal, SimpleGrid, useMantineTheme } from '@mantine/core';
import { api } from '../apiClient';
import FilterView from '../components/FilterView';
import QuestionView from '../components/QuestionView';
import ZoomModal from '../components/ZoomModal';
import {
  parseSelectableValues,
  toSortedUniqueList,
  calculateProgressData,
} from '../utils/questionDataUtils';

const BASE = 'https://docs.aws.amazon.com/ja_jp/aws-certification/latest';
const EXAM_GUIDE_URLS = {
  aifc01: `${BASE}/ai-practitioner-01/ai-practitioner-01.html`,
  aipc01: `${BASE}/ai-professional-01/ai-professional-01.html`,
  ansc01: `${BASE}/advanced-networking-specialty-01/advanced-networking-specialty-01.html`,
  clfc02: `${BASE}/cloud-practitioner-02/cloud-practitioner-02.html`,
  deac01: `${BASE}/data-engineer-associate-01/data-engineer-associate-01.html`,
  dopc02: `${BASE}/devops-engineer-professional-02/devops-engineer-professional-02.html`,
  dvac02: `${BASE}/developer-associate-02/developer-associate-02.html`,
  mlac01: `${BASE}/machine-learning-engineer-associate-01/machine-learning-engineer-associate-01.html`,
  mlsc01: `${BASE}/machine-learning-specialty-01/machine-learning-specialty-01.html`,
  saac03: `${BASE}/solutions-architect-associate-03/solutions-architect-associate-03.html`,
  sapc02: `${BASE}/solutions-architect-professional-02/solutions-architect-professional-02.html`,
  soac03: `${BASE}/sysops-administrator-associate-03/sysops-administrator-associate-03.html`,
};

function ExamView({
  selectedCertification,
  setSelectedCertification,
  user,
}) {
  const [questions, setQuestions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [results, setResults] = useState({});
  const [progressData, setProgressData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(true);
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedTask, setSelectedTask] = useState([]);
  const [selectedResult, setSelectedResult] = useState([]);
  const [questionOrder, setQuestionOrder] = useState('first');
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [resumeData, setResumeData] = useState(null);
  const [certificationId, setCertificationId] = useState(null);
  const [certFormalName, setCertFormalName] = useState(null);
  const [certVendor, setCertVendor] = useState(null);
  const [certUserStatus, setCertUserStatus] = useState(null);

  const [gridModalOpen, setGridModalOpen] = useState(false);

  const theme = useMantineTheme();

  const saveTimerRef = useRef(null);

  // ステータス表示用のラベルマップ（保存値はコードを使用）
  const statusLabelMap = {
    correct: '正解',
    incorrect: '不正解',
    excluded: '除外',
  };

  // フィルターの表示ラベルから保存用コードへ変換するマップ
  const statusCodeMap = {
    正解: 'correct',
    不正解: 'incorrect',
    除外: 'excluded',
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        console.log('ユーザーがログインしていません');
        setLoading(false);
        return;
      }

      // 資格コードから certifications.id を解決する
      let certificationData;
      try {
        certificationData = await api.get(`/certifications/${selectedCertification}`);
      } catch (e) {
        console.error('資格IDの取得に失敗しました:', e);
        setLoading(false);
        return;
      }

      if (!certificationData?.id) {
        console.error('資格IDが取得できませんでした');
        setLoading(false);
        return;
      }

      setCertificationId(certificationData.id);
      setCertFormalName(certificationData.formal_name ?? null);
      setCertVendor(certificationData.vendor ?? null);
      setCertUserStatus(certificationData.user_status ?? null);

      // questions をユーザーの回答ステータス付きで取得する
      let questionData;
      try {
        questionData = await api.get('/questions', { certificationId: certificationData.id });
      } catch (e) {
        console.error('データ取得エラー:', e);
        return;
      }

      // 問題IDごとのステータスマップを生成する（user_status フィールドから取得）
      const resultMap = {};
      const normalizedQuestions = (questionData || []).map((question) => {
        if (question.user_status) {
          resultMap[question.id] = question.user_status;
        }
        // eslint-disable-next-line no-unused-vars
        const { user_status, ...rest } = question;
        return rest;
      });

      setAllQuestions(normalizedQuestions);
      setQuestions(normalizedQuestions);
      setResults(resultMap);

      const categoryNames = toSortedUniqueList(
        (normalizedQuestions || []).flatMap((question) => parseSelectableValues(question?.category))
      );
      setCategoryOptions(categoryNames);
      setSelectedCategory([]);

      const taskNames = toSortedUniqueList(
        (normalizedQuestions || []).flatMap((question) => parseSelectableValues(question?.task))
      );
      setTaskOptions(taskNames);
      setSelectedTask([]);

      const progress = calculateProgressData(normalizedQuestions, resultMap, categoryNames, taskNames);
      setProgressData(progress);

      setSelectedQuestions([]);

      // localStorage を高速パスとして先に読み込む（同デバイス向け）
      const lsKey = `lastQuestion_${selectedCertification}`;
      const lsFilterKey = `lastFilter_${selectedCertification}`;
      const lsId = localStorage.getItem(lsKey);
      const lsFilter = localStorage.getItem(lsFilterKey);
      if (lsId && normalizedQuestions.some(q => String(q.id) === lsId)) {
        setResumeData({
          questionId: lsId,
          filterState: lsFilter ? JSON.parse(lsFilter) : {},
        });
      }

      // API から取得してクロスデバイス対応（より新しいデータで上書き）
      try {
        const apiResume = await api.get('/user-resume-position', { certCode: selectedCertification });
        if (apiResume?.question_id) {
          const exists = normalizedQuestions.some(q => q.id === apiResume.question_id);
          if (exists) {
            setResumeData({
              questionId: String(apiResume.question_id),
              filterState: apiResume.filter_state ?? {},
            });
          }
        }
      } catch {}

      setLoading(false);
    };

    fetchData();
  }, [selectedCertification, user]);

  // 問題表示中に現在の問題ID・フィルター状態を保存する
  useEffect(() => {
    if (!showFilter && questions.length > 0 && questions[currentIndex] && certificationId) {
      const questionId = questions[currentIndex].id;
      const filterState = {
        selectedTopic,
        selectedCategory,
        selectedTask,
        selectedResult,
        questionOrder,
        selectedQuestions,
      };

      // localStorage: 即時保存（同デバイス高速パス）
      localStorage.setItem(`lastQuestion_${selectedCertification}`, String(questionId));
      localStorage.setItem(`lastFilter_${selectedCertification}`, JSON.stringify(filterState));

      // API: デバウンス2秒（クロスデバイス用）
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        api.put('/user-resume-position', {
          certificationId,
          questionId,
          filterState,
        }).catch(() => {});
      }, 2000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, showFilter, questions, certificationId, selectedCertification]);

  // フィルター条件を適用して問題リストを返すヘルパー（handleStart / handleResume 共通）
  const buildFilteredList = (filterOverrides = {}) => {
    const topic    = filterOverrides.selectedTopic    ?? selectedTopic;
    const category = filterOverrides.selectedCategory ?? selectedCategory;
    const task     = filterOverrides.selectedTask     ?? selectedTask;
    const qs       = filterOverrides.selectedQuestions ?? selectedQuestions;
    const result   = filterOverrides.selectedResult   ?? selectedResult;

    let filtered = allQuestions;

    if (Array.isArray(topic) && topic.length > 0) {
      filtered = filtered.filter(q => topic.includes(q.topic?.toString()));
    }

    if (Array.isArray(category) && category.length > 0) {
      filtered = filtered.filter(q => {
        const categories = parseSelectableValues(q?.category);
        return categories.some(c => category.includes(c));
      });
    }

    if (Array.isArray(task) && task.length > 0) {
      filtered = filtered.filter(q => {
        const tasks = parseSelectableValues(q?.task);
        return tasks.some(t => task.includes(t));
      });
    }

    if (Array.isArray(qs) && qs.length > 0) {
      filtered = filtered.filter(q => qs.includes(q.number?.toString()));
    }

    if (Array.isArray(result) && result.length > 0) {
      const selectedCodes = result.map((label) => statusCodeMap[label]).filter(Boolean);
      const includeUnanswered = result.includes('未回答');
      filtered = filtered.filter(q => {
        const r = results[q.id];
        if (!r) return includeUnanswered;
        return selectedCodes.includes(r);
      });
    }

    filtered.sort((a, b) => {
      const topicCompare = a.topic?.toString().localeCompare(b.topic?.toString(), 'ja', { numeric: true });
      if (topicCompare !== 0) return topicCompare;
      return a.number?.toString().localeCompare(b.number?.toString(), 'ja', { numeric: true });
    });

    return filtered;
  };

  const handleStart = (startPosition = 1) => {
    const filtered = buildFilteredList();
    setQuestions(filtered);

    if (questionOrder === 'last' && filtered.length > 0) {
      setCurrentIndex(filtered.length - 1);
    } else {
      const idx = Math.max(0, Math.min((startPosition || 1) - 1, filtered.length - 1));
      setCurrentIndex(idx);
    }

    setShowAnswer(false);
    setShowFilter(false);
  };

  // 前回のフィルター設定を復元し、保存された問題位置から再開する
  const handleResume = () => {
    if (!resumeData) return;
    const { questionId, filterState } = resumeData;

    // フィルターUI状態を復元する
    setSelectedTopic(filterState.selectedTopic ?? []);
    setSelectedCategory(filterState.selectedCategory ?? []);
    setSelectedTask(filterState.selectedTask ?? []);
    setSelectedResult(filterState.selectedResult ?? []);
    setQuestionOrder(filterState.questionOrder ?? 'first');
    setSelectedQuestions(filterState.selectedQuestions ?? []);

    // 保存されたフィルターを直接適用して問題リストを構築し、前回の問題へジャンプする
    const filtered = buildFilteredList(filterState);
    setQuestions(filtered);
    const idx = filtered.findIndex(q => String(q.id) === questionId);
    setCurrentIndex(idx >= 0 ? idx : 0);
    setShowAnswer(false);
    setShowFilter(false);
  };

  const updateStatus = async (id, result) => {
    if (!user) {
      console.error('ユーザーがログインしていません');
      return;
    }

    // user_id と question_id の複合キーで upsert する
    try {
      await api.put('/user-question-statuses', { question_id: id, status: result });
    } catch (e) {
      console.error('ステータス更新エラー:', e);
      return;
    }

    {
      setResults(prev => {
        const updatedResults = {
          ...prev,
          [id]: result
        };

        // 進捗データを再計算（除外も含めた集計を再利用）
        if (allQuestions && allQuestions.length > 0) {
          const progress = calculateProgressData(
            allQuestions,
            updatedResults,
            categoryOptions,
            taskOptions
          );
          setProgressData(progress);
        }

        return updatedResults;
      });
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowAnswer(false);
    }
  };

  const handlePrev = (backOne = true) => {
    if (backOne && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowAnswer(false);
    } else {
      setShowFilter(true);
      setShowAnswer(false);
      setCurrentIndex(0);
    }
  };

  const handleOpenGrid = () => setGridModalOpen(true);

  const handleNextUnanswered = () => {
    // 現在位置+1 から末尾へ探索
    for (let i = currentIndex + 1; i < questions.length; i++) {
      if (!results[questions[i].id]) {
        setCurrentIndex(i);
        setShowAnswer(false);
        return true;
      }
    }
    // 先頭から現在位置の手前まで循環探索
    for (let i = 0; i < currentIndex; i++) {
      if (!results[questions[i].id]) {
        setCurrentIndex(i);
        setShowAnswer(false);
        return true;
      }
    }
    return false;
  };

  const handleImageClick = (url) => setZoomImageUrl(url);
  const closeZoom = () => setZoomImageUrl(null);

  const handleSetCurrentQuestion = (updatedQuestion) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => (q.id === updatedQuestion.id ? updatedQuestion : q))
    );
    setAllQuestions((prevAllQuestions) =>
      prevAllQuestions.map((q) =>
        q.id === updatedQuestion.id ? updatedQuestion : q
      )
    );
  };

  if (loading) return (
    <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem' }}>
      <Text c="dimmed">読み込み中...</Text>
    </Box>
  );

  return (
    <>
      <ZoomModal zoomImageUrl={zoomImageUrl} onClose={closeZoom} />

      {/* 問題一覧グリッドモーダル */}
      {questions.length > 0 && (() => {
        const topicsSet = new Set(questions.map(q => q.topic?.toString()));
        const hasMultipleTopics = topicsSet.size > 1;
        return (
          <Modal
            opened={gridModalOpen}
            onClose={() => setGridModalOpen(false)}
            title="問題一覧"
            size="lg"
          >
            <Box style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <SimpleGrid cols={10} spacing="xs" verticalSpacing="xs">
                {questions.map((q, idx) => {
                  const status = results[q.id];
                  const isCurrent = idx === currentIndex;
                  const label = hasMultipleTopics
                    ? `T${q.topic}-${q.number}`
                    : `${q.number}`;

                  let color, variant;
                  if (status === 'correct')       { color = 'green'; variant = 'filled'; }
                  else if (status === 'incorrect') { color = 'red';   variant = 'filled'; }
                  else if (status === 'excluded')  { color = 'gray';  variant = 'filled'; }
                  else                             { color = theme.primaryColor; variant = 'outline'; }

                  return (
                    <Button
                      key={q.id}
                      size="xs"
                      color={color}
                      variant={variant}
                      style={{
                        minWidth: 0,
                        paddingLeft: 4,
                        paddingRight: 4,
                        height: 30,
                        fontSize: '11px',
                        outline: isCurrent ? '2px solid var(--mantine-primary-color-filled)' : undefined,
                        outlineOffset: isCurrent ? '1px' : undefined,
                      }}
                      onClick={() => {
                        setCurrentIndex(idx);
                        setShowAnswer(false);
                        setGridModalOpen(false);
                      }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </SimpleGrid>
            </Box>

            {/* 凡例 */}
            <Group gap="sm" mt="sm" justify="center">
              <Group gap={4}>
                <Box style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--mantine-color-green-6)' }} />
                <Text size="xs" c="dimmed">正解</Text>
              </Group>
              <Group gap={4}>
                <Box style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--mantine-color-red-6)' }} />
                <Text size="xs" c="dimmed">不正解</Text>
              </Group>
              <Group gap={4}>
                <Box style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--mantine-color-gray-5)' }} />
                <Text size="xs" c="dimmed">除外</Text>
              </Group>
              <Group gap={4}>
                <Box style={{ width: 12, height: 12, borderRadius: 2, border: '1px solid var(--mantine-color-gray-5)' }} />
                <Text size="xs" c="dimmed">未回答</Text>
              </Group>
            </Group>
          </Modal>
        );
      })()}
      {showFilter ? (
        <FilterView
          questions={allQuestions}
          results={results}
          selectedTopic={selectedTopic}
          setSelectedTopic={setSelectedTopic}
          categories={categoryOptions}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          tasks={taskOptions}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
          selectedResult={selectedResult}
          setSelectedResult={setSelectedResult}
          questionOrder={questionOrder}
          setQuestionOrder={setQuestionOrder}
          selectedCertification={selectedCertification}
          setSelectedCertification={setSelectedCertification}
          selectedQuestions={selectedQuestions}
          setSelectedQuestions={setSelectedQuestions}
          progressData={progressData}
          onStart={handleStart}
          lastQuestionId={resumeData?.questionId}
          resumeFilterState={resumeData?.filterState}
          onResume={handleResume}
          examGuideUrl={EXAM_GUIDE_URLS[selectedCertification] ?? null}
          certFormalName={certFormalName}
          certVendor={certVendor}
          certUserStatus={certUserStatus}
        />
      ) : questions.length === 0 ? (
        <Center py="4rem">
          <Stack align="center" gap="md">
            <Text c="dimmed">問題が見つかりませんでした。</Text>
            <Button variant="outline" onClick={() => setShowFilter(true)}>
              フィルターに戻る
            </Button>
          </Stack>
        </Center>
      ) : (
        <QuestionView
          currentQuestion={questions[currentIndex]}
          currentIndex={currentIndex}
          totalQuestions={questions.length}
          results={results}
          onSetResult={updateStatus}
          onNext={handleNext}
          onPrev={handlePrev}
          onImageClick={handleImageClick}
          showAnswer={showAnswer}
          toggleShowAnswer={() => setShowAnswer(!showAnswer)}
          onSetCurrentQuestion={handleSetCurrentQuestion}
          selectedCertification={selectedCertification}
          questionOrder={questionOrder}
          user={user}
          statusLabelMap={statusLabelMap}
          gridModalOpen={gridModalOpen}
          onOpenGrid={handleOpenGrid}
          onNextUnanswered={handleNextUnanswered}
        />
      )}
    </>
  );
}

export default ExamView;
